import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import showErrorToast, { showSuccessToast } from "../../../components/CustomComponents/toast/toast";
import Preloader from "../../../components/CustomComponents/preLoad/preLoader";
import useDarkMode from "../../../hooks/useDarkMode";
import useThemeColor from "../../../hooks/useThemeColor";
import swal from "sweetalert";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../../staff/dropdownStyles";
import { SortOrderDropDownSelection } from "../../../components/CustomComponents/DropdownSelections/DropDownSelections";
import ColumnSelection from "../../../components/CustomComponents/ColumnSelection/ColumnSelection";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
} from "../../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../../utils/preferencesUtils";
import "./../../order/ManageOrderFilters.css";

const ManageBranches = () => {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const profileData = useSelector((state) => state.auth.profileData);
  const parentComID = profileData?.parentComID ?? profileData?.ParentComID;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedItems, setSelectedItems] = useState(new Set());
  const lastSelectedIndex = useRef(null);

  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const themeColor = useThemeColor();

  const pageKey = "branches";
  const defaultVisibleColumns = {
    branchName: true,
    email: true,
    phone: true,
    address: true,
    staffCount: true,
    productCount: true,
    status: true,
  };
  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = { value: "CompName", label: "Branch Name" };

  const sortByOptions = [
    { value: "CompName", label: "Branch Name" },
    { value: "CompEmail", label: "Email" },
    { value: "CompPhone", label: "Phone" },
    { value: "StaffCount", label: "Staff Count" },
    { value: "ProductCount", label: "Product Count" },
  ];

  const preferences = useSelector(
    (state) => state.itemGridPreferences[pageKey] || {}
  );
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;

  useEffect(() => {
    // Check if preferences exist and merge with defaults
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
  }, [dispatch, pageKey]);

  const fetchBranches = useCallback(async () => {
    if (!CompID) return;

    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_BRANCHES);

      if (response.data && response.data.status && response.data.data) {
        const branchesData = Array.isArray(response.data.data) ? response.data.data : [];
        setBranches(branchesData);
      } else {
        setBranches([]);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      showErrorToast("Failed to load branches");
      setBranches([]);
    }
  }, [CompID]);

  const filteredBranches = branches.filter((branch) => {
    if (!search.trim()) return true;
    const searchLower = search.toLowerCase();
    return (
      branch.compName?.toLowerCase().includes(searchLower) ||
      branch.compEmail?.toLowerCase().includes(searchLower) ||
      branch.compPhone?.toLowerCase().includes(searchLower) ||
      branch.compAddress?.toLowerCase().includes(searchLower)
    );
  });

  useEffect(() => {
    // Wait until profile is loaded before deciding parent vs branch access
    if (parentComID == null) return;
    if (parentComID !== 0) {
      navigate("/setting");
      return;
    }
    
    fetchBranches();
  }, [fetchBranches, parentComID, navigate]);

  const handleSelectItem = (branchId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (filteredBranches[i]?.compID) {
            newSelected.add(filteredBranches[i].compID);
          }
        }
      } else {
        if (newSelected.has(branchId)) {
          newSelected.delete(branchId);
        } else {
          newSelected.add(branchId);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(filteredBranches.map((item) => item.compID));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleEdit = (branchId) => {
    navigate(`/branches/update/${branchId}`);
  };

  const locationLicenceLimit = profileData?.LocationLicences ?? profileData?.locationLicences ?? 0;
  const canCreateBranch = locationLicenceLimit <= 0 || branches.length < locationLicenceLimit;

  const handleCreateBranchClick = (e) => {
    if (!canCreateBranch) {
      e.preventDefault();
      showErrorToast(
        `Location license limit reached (${branches.length}/${locationLicenceLimit}). You cannot create more branches. Please contact support to increase your plan.`
      );
      return;
    }
    navigate("/branches/create");
  };

  const handleDelete = async (branch) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete branch "${branch.compName}"? This will permanently delete:\n\n• All product assignments\n• Receipt settings\n• Company settings\n• Service hours/timing\n• Inventory stock\n• All other branch-related data\n\nThis action cannot be undone!`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) {
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.delete(`${APIs.DELETE_BRANCH}/${branch.compID}`);

      if (response.data && response.data.status) {
        showSuccessToast(response.data.message || "Branch deleted successfully");
        // Refresh the branches list
        await fetchBranches();
        // Clear selection if deleted branch was selected
        setSelectedItems((prev) => {
          const newSelected = new Set(prev);
          newSelected.delete(branch.compID);
          return newSelected;
        });
      } else {
        showErrorToast(response.data?.message || "Failed to delete branch");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to delete branch";
      showErrorToast(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
  };

  const renderNoDataMessage = () => {
    if (loading) return null;
    if (filteredBranches.length === 0 && !search.trim()) {
      return (
        <div className="alert alert-info text-center">
          <i className="fa-solid fa-code-branch fa-3x mb-3" style={{ opacity: 0.3 }}></i>
          <p className="mb-0">No branches found. Create your first branch to get started.</p>
        </div>
      );
    }
    if (filteredBranches.length === 0 && search.trim()) {
      return (
        <div className="alert alert-info text-center">
          <p className="mb-0">No branches match your search criteria.</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/setting">Settings</Link>
            </li>
            <li className="breadcrumb-item active">Branch Management</li>
          </ol>
          <h1 className="page-header mb-0">Manage Your Branches Efficiently</h1>
        </div>
        <div className="ms-auto">
          <button
            type="button"
            className="btn px-4"
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: 'none',
              color: 'white',
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              boxShadow: `0 2px 8px ${themeColor}40`,
              opacity: canCreateBranch ? 1 : 0.65,
              cursor: canCreateBranch ? 'pointer' : 'not-allowed',
            }}
            onClick={handleCreateBranchClick}
            disabled={!canCreateBranch}
            title={!canCreateBranch ? `Location license limit reached (${branches.length}/${locationLicenceLimit})` : ''}
            onMouseEnter={(e) => {
              if (canCreateBranch) {
                e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
            }}
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-white"></i>
            Create New Branch
          </button>
        </div>
      </div>

      <div className="mb-3 mt-2">
        <div className="card border-0">
          <div className="tab-content p-3">
            <div className="tab-pane fade show active" id="allTab">
              {/* Enhanced Filter Section */}
              <div className="mb-4">
                <div
                  className="card filter-card"
                  style={{
                    position: "relative",
                    zIndex: 1,
                    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: `0 4px 20px ${themeColor}40`
                  }}
                >
                  <div className="card-body p-3">
                    {/* Filter Controls */}
                    <div className="row g-3 align-items-center">
                      {/* Search Field */}
                      <div className="col-xl-4 col-lg-4 col-md-6">
                        <div className="search-input filter-control">
                          <input
                            type="text"
                            className="filter-input"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by name, email, phone, address..."
                            style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                              color: '#333'
                            }}
                          />
                        </div>
                      </div>

                      {/* Sort By Dropdown */}
                      <div className="col-xl-3 col-lg-3 col-md-6">
                        <div className="filter-control">
                          <Select
                            value={sortBy}
                            onChange={(val) => dispatch(setSortBy({ pageKey, sortBy: val }))}
                            options={sortByOptions}
                            styles={dropdownStyles}
                            placeholder="Sort By"
                            className="filter-dropdown"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            menuShouldBlockScroll={false}
                            menuShouldScrollIntoView={false}
                            closeMenuOnScroll={false}
                          />
                        </div>
                      </div>

                      {/* Sort Order Dropdown */}
                      <div className="col-xl-2 col-lg-2 col-md-6">
                        <div className="filter-control">
                          <SortOrderDropDownSelection
                            defaultValue={sortOrder}
                            onChange={(val) => dispatch(setSortOrder({ pageKey, sortOrder: val }))}
                          />
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="col-xl-3 col-lg-3 col-md-12">
                        <div className="d-flex gap-2">
                          <button
                            className="clear-btn"
                            onClick={clearFilters}
                            title="Clear all filters"
                          >
                            <i className="fa fa-times me-1"></i>
                            Filter
                          </button>

                          <ColumnSelection
                            visibleColumns={visibleColumns}
                            setVisibleColumns={(cols) =>
                              dispatch(setVisibleColumns({ pageKey, columns: cols }))
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Selection Info and Actions */}
              {selectedItems.size > 0 && (
                <div className="mb-3">
                  <div className="p-3 bg-light rounded d-flex align-items-center justify-content-between" style={{ border: "1px solid #dee2e6" }}>
                    <div className="d-flex align-items-center gap-3">
                      <span className="fw-bold text-primary">
                        {selectedItems.size} branch{selectedItems.size > 1 ? 'es' : ''} selected
                      </span>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleDeselectAll}
                      >
                        Clear Selection
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {renderNoDataMessage()}

              <div
                className="table-responsive mt-3"
                style={{
                  minHeight: "200px",
                  maxHeight: "450px",
                  overflowY: "auto",
                  overflowX: "auto",
                }}
              >
                <table className="table table-hover">
                  <thead
                    className="bg-light"
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 5,
                      backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  >
                    <tr>
                      <th className="pt-2 pb-2">
                        <input
                          type="checkbox"
                          onChange={handleSelectAll}
                          checked={
                            filteredBranches.length > 0 &&
                            selectedItems.size === filteredBranches.length
                          }
                        />
                      </th>
                      {visibleColumns.branchName && (
                        <th className="pt-2 pb-2 text-center">Branch Name</th>
                      )}
                      {visibleColumns.email && (
                        <th className="pt-2 pb-2 text-center">Email</th>
                      )}
                      {visibleColumns.phone && (
                        <th className="pt-2 pb-2 text-center">Phone</th>
                      )}
                      {visibleColumns.address && (
                        <th className="pt-2 pb-2 text-center">Address</th>
                      )}
                      {visibleColumns.staffCount && (
                        <th className="pt-2 pb-2 text-center">Staff Count</th>
                      )}
                      {visibleColumns.productCount && (
                        <th className="pt-2 pb-2 text-center">Products Count</th>
                      )}
                      {visibleColumns.status && (
                        <th className="pt-2 pb-2 text-center">Status</th>
                      )}
                      <th className="pt-2 pb-2 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBranches.map((branch, index) => (
                      <tr
                        key={branch.compID}
                        style={{
                          backgroundColor: selectedItems.has(branch.compID)
                            ? (isDarkMode ? "#3d4448" : "#e3f2fd")
                            : (isDarkMode ? "#2d3436" : "#ffffff"),
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <td className="pt-2 pb-2">
                          <input
                            type="checkbox"
                            checked={selectedItems.has(branch.compID)}
                            onChange={(e) => handleSelectItem(branch.compID, index, e)}
                          />
                        </td>
                        {visibleColumns.branchName && (
                          <td className="text-center">
                            <strong>{branch.compName || "N/A"}</strong>
                          </td>
                        )}
                        {visibleColumns.email && (
                          <td className="text-center">{branch.compEmail || "N/A"}</td>
                        )}
                        {visibleColumns.phone && (
                          <td className="text-center">{branch.compPhone || "N/A"}</td>
                        )}
                        {visibleColumns.address && (
                          <td className="text-center">
                            {branch.compAddress
                              ? `${branch.compAddress}${branch.compCity ? `, ${branch.compCity}` : ""}`
                              : "N/A"}
                          </td>
                        )}
                        {visibleColumns.staffCount && (
                          <td className="text-center">{branch.staffCount || 0}</td>
                        )}
                        {visibleColumns.productCount && (
                          <td className="text-center">{branch.productCount || 0}</td>
                        )}
                        {visibleColumns.status && (
                          <td className="text-center">
                            {branch.compPublish ? (
                              <span
                                className="badge"
                                style={{
                                  backgroundColor: "#27ae60",
                                  color: "#ffffff",
                                }}
                              >
                                Active
                              </span>
                            ) : (
                              <span
                                className="badge"
                                style={{
                                  backgroundColor: "#95a5a6",
                                  color: "#ffffff",
                                }}
                              >
                                Inactive
                              </span>
                            )}
                          </td>
                        )}
                        <td className="text-center">
                          <div className="btn-group" role="group">
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={() => handleEdit(branch.compID)}
                              title="Edit Branch"
                            >
                              <i className="fa-solid fa-edit"></i>
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDelete(branch)}
                              title="Delete Branch"
                            >
                              <i className="fa-solid fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageBranches;
